import api from "@/lib/api";
import { readConsent } from "@/lib/cookieConsent";

/**
 * Destination recommendations (server: /api/recommendations/*).
 *
 * The server knows the visitor's country from the address and reads its fare
 * cache only. Simultaneous callers share a request; later loads revalidate
 * server-owned versions instead of pinning old fares in sessionStorage.
 * The origin is sent only once the visitor picked one (remembered).
 */
export const ORIGIN_KEY = "toukky_reco_origin";
const inFlight = new Map();
const BUDGET_USD = 500;

function storage(kind) {
  try { return kind === "local" ? window.localStorage : window.sessionStorage; } catch { return null; }
}

export function rememberedOrigin() {
  try { return storage("local")?.getItem(ORIGIN_KEY) || ""; } catch { return ""; }
}

export function rememberOrigin(code) {
  try { if (code) storage("local")?.setItem(ORIGIN_KEY, code); else storage("local")?.removeItem(ORIGIN_KEY); } catch { /* blocked */ }
}

/** A round budget in the visitor's currency (about 500 USD), and the same in XOF for the server. */
export function niceBudget(code, currencies) {
  const rate = currencies?.[code]?.xof_per_unit;
  const usd = currencies?.USD?.xof_per_unit;
  if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(usd) || usd <= 0) return { amount: null, xof: null };
  const value = (BUDGET_USD * usd) / rate;
  const step = 10 ** Math.floor(Math.log10(value)) / 2;
  const amount = Math.max(step, Math.floor(value / step) * step);
  return { amount, xof: Math.round(amount * rate) };
}

export async function fetchHome({ origin, budget } = {}) {
  const params = {};
  if (origin) params.origin = origin;
  if (budget) params.budget = budget;
  const key = `${origin || "auto"}:${budget || ""}`;
  if (inFlight.has(key)) return inFlight.get(key);
  const request = api.get("/recommendations/home", { params }).then(({ data }) => data);
  inFlight.set(key, request);
  try { return await request; } finally { inFlight.delete(key); }
}

/** A snapshot can expire while the page stays open. Never mutate shared data. */
export function freshResponse(data, now = Date.now()) {
  if (!data) return data;
  return { ...data, sections: (data.sections || []).map((section) => ({ ...section,
    items: (section.items || []).filter((card) => Number.isFinite(card.price_xof) && card.price_xof > 0
      && Date.parse(card.expires_at) > now),
  })) };
}

const seen = new Set();

/** Fire and forget: a card seen (once per page view) or opened. Never throws. */
export function track(type, { section, origin, destination }) {
  if (!destination || readConsent() !== "accepted") return;
  if (type === "impression") {
    const id = `${section}:${origin}:${destination}`;
    if (seen.has(id)) return;
    if (seen.size >= 500) seen.clear();
    seen.add(id);
  }
  try {
    Promise.resolve(api.post("/recommendations/events", { type, section, origin: origin || null, destination }))
      .catch(() => {});
  } catch { /* never breaks the page */ }
}

/** Link to the flight results for a card, as the card's own search says. */
export function searchLink(search) {
  const q = new URLSearchParams();
  for (const k of ["origin_city", "destination_city", "date", "return_date", "trip"]) {
    const value = search?.[k];
    if (typeof value === "string" && value.length <= 64 && value) q.set(k, value);
  }
  return `/flights?${q.toString()}`;
}
